import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission (will be replaced with real API)
    setTimeout(() => {
      toast({
        title: "Message Sent Successfully!",
        description: "We'll get back to you within 24 hours.",
      });
      
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        service: '',
        message: ''
      });
      setIsSubmitting(false);
    }, 1500);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "Email Us",
      value: "sales@aiflo.ai",
      link: "mailto:sales@aiflo.ai"
    },
    {
      icon: Phone,
      title: "Call Us",
      value: "+971 54 478 5924",
      link: "tel:+971544785924"
    },
    {
      icon: MapPin,
      title: "Visit Us",
      value: "Global Presence - 25 Countries",
      link: "#"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50/40 via-white to-gray-50/60" />
        
        <div className="relative max-w-7xl mx-auto text-center">
          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Let's Build Something
            <span className="bg-gradient-to-r from-gray-900 to-orange-400 bg-clip-text text-transparent"> Extraordinary</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            Ready to transform your enterprise? Our experts are here to help you navigate your digital transformation journey.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {contactInfo.map((info, index) => (
              <Card 
                key={index}
                className="p-8 text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-2 border-transparent hover:border-orange-200"
              >
                <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <info.icon className="w-7 h-7 text-orange-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{info.title}</h3>
                <a 
                  href={info.link}
                  className="text-gray-600 hover:text-orange-600 transition-colors"
                >
                  {info.value}
                </a>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <Card className="p-12 bg-white shadow-2xl border-2 border-orange-100">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Left Column - Info */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Start Your Transformation Today
                </h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  Fill out the form and one of our enterprise solution specialists will reach out to discuss your needs.
                </p>

                <div className="space-y-6 mb-8">
                  {[
                    "Free initial consultation",
                    "Customized solution recommendations",
                    "No-obligation assessment",
                    "24-hour response time"
                  ].map((benefit, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-orange-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-700 text-lg">{benefit}</span>
                    </div>
                  ))}
                </div>

                <Card className="p-6 bg-gradient-to-br from-orange-50 to-gray-50 border-l-4 border-orange-400">
                  <p className="text-sm text-gray-700 italic leading-relaxed">
                    "AiFlo.ai's team was incredibly responsive and knowledgeable. They understood our challenges immediately and proposed a solution that exceeded our expectations."
                  </p>
                  <p className="text-sm text-gray-900 font-semibold mt-3">
                    - CTO, Fortune 500 Company
                  </p>
                </Card>
              </div>

              {/* Right Column - Form */}
              <div>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-gray-900 font-medium mb-2 block">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      placeholder="John Doe"
                      className="w-full px-4 py-6 rounded-xl border-2 border-gray-200 focus:border-orange-400"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-gray-900 font-medium mb-2 block">
                      Business Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      placeholder="john@company.com"
                      className="w-full px-4 py-6 rounded-xl border-2 border-gray-200 focus:border-orange-400"
                    />
                  </div>

                  <div>
                    <Label htmlFor="company" className="text-gray-900 font-medium mb-2 block">
                      Company Name *
                    </Label>
                    <Input
                      id="company"
                      type="text"
                      required
                      value={formData.company}
                      onChange={(e) => handleChange('company', e.target.value)}
                      placeholder="Your Company Inc."
                      className="w-full px-4 py-6 rounded-xl border-2 border-gray-200 focus:border-orange-400"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-gray-900 font-medium mb-2 block">
                      Phone Number
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      placeholder="+1 (234) 567-890"
                      className="w-full px-4 py-6 rounded-xl border-2 border-gray-200 focus:border-orange-400"
                    />
                  </div>

                  <div>
                    <Label htmlFor="service" className="text-gray-900 font-medium mb-2 block">
                      Service Interest *
                    </Label>
                    <Select value={formData.service} onValueChange={(value) => handleChange('service', value)}>
                      <SelectTrigger className="w-full px-4 py-6 rounded-xl border-2 border-gray-200">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ai-automation">AI & Automation</SelectItem>
                        <SelectItem value="cloud">Cloud & Infrastructure</SelectItem>
                        <SelectItem value="data">Data & Analytics</SelectItem>
                        <SelectItem value="integration">Enterprise Integration</SelectItem>
                        <SelectItem value="no-code">No-Code/Low-Code</SelectItem>
                        <SelectItem value="consulting">General Consulting</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-gray-900 font-medium mb-2 block">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      required
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      placeholder="Tell us about your project and requirements..."
                      rows={5}
                      className="w-full px-4 py-4 rounded-xl border-2 border-gray-200 focus:border-orange-400 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gray-900 hover:bg-gray-800 text-white py-6 text-lg rounded-xl shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        Send Message
                        <Send className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>

                  <p className="text-sm text-gray-500 text-center">
                    By submitting this form, you agree to our Privacy Policy and Terms of Service.
                  </p>
                </form>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Map Section (Placeholder) */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Global Presence
            </h2>
            <p className="text-xl text-gray-600">
              Serving enterprises across 25 countries
            </p>
          </div>

          <Card className="h-96 bg-gradient-to-br from-orange-100 to-gray-100 flex items-center justify-center">
            <div className="text-center">
              <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-xl text-gray-600">Interactive Map Coming Soon</p>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Contact;
